import { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  ArrowUpRight,
  Bot,
  ChevronLeft,
  ChevronRight,
  CornerUpRight,
  Flag,
  Mail,
  MailOpen,
  MessageSquare,
  Search,
  Send,
  Star,
  Trash2,
  TrendingUp,
  UserMinus,
  X,
} from 'lucide-react'

import confirm from 'components/dialogConfirm'
import { cn } from 'components/UI/cn'
import { StyledEditorRHF } from 'components/Editor/EditorRHF'
import { ReactCreatableSelectWrapper } from 'components/FormUtils/ReactCreatableSelectWrapper'
import {
  buildReplyAllCcOptions,
  CcBccToggles,
  emailRecipientStyles,
  filterSelf,
  formatEmailRecipientOption,
  looksLikeEmail,
  RecipientRow,
  ReplyMode,
  toWireRecipients,
} from 'components/FormUtils/EmailRecipientSelect'
import {
  InlineComposeShell,
  InlineReplyHeader,
  ThreadMessageCard,
  ThreadMessageData,
} from 'components/Email'
import { useAuth } from 'context/authentication/useAuth'
import { RecipientOption, useRecipientOptions } from 'hooks/useRecipientOptions'
import {
  SectionedMultiSelect,
  SectionedSection,
} from 'components/UI/Select/SectionedMultiSelect'
import { useGetCampaigns } from 'features/campaigns/campaigns_api'
import { formatRelativeTime } from 'utils/formatting'
import { toast } from 'react-toastify'
import axios from 'axios'
import {
  ForwardReplyPayload,
  getReplyContactCompany,
  getReplyThreadInboxLink,
  Reply,
  ReplyContactCompany,
  ReplyFilters,
  ReplySummary,
  ReplyToReplyPayload,
  useForwardReply,
  useGetReplyThreadsInfinite,
  useGetReplyThreadSummary,
  useGetThreadReplies,
  useDeleteReply,
  useMarkReplyAutomated,
  useMarkReplyInterested,
  useMarkReplyRead,
  useReplyToReply,
  useToggleThreadFlag,
  useUnsubscribeReplyContact,
  useTakeOver,
  TakeOverPayload,
} from 'features/campaigns/replies_api'
import { useGetEmailIntegrationOptions } from 'services/emailIntegrationApi'
import { TakeOverModal } from 'features/campaigns/TakeOverModal'
import { useCompanyV2Url } from 'utils/useCompanyUrl'
import { CompanyTypeSlug } from 'models/companies'
import { useGetCampaignSequence } from 'features/campaigns/sequences_api'
import { useGetWorkspaceSenderEmails } from 'features/campaigns/sender_emails_api'
import { useInfiniteScroll } from 'hooks/useInfiniteScroll'

/**
 * Lazy-loaded so the (large) deal form isn't in the critical bundle for the
 * campaign replies tab — it only renders when the user triggers Create Deal.
 */
const CreateDealModal = lazy(
  () => import('components/Deals/CreateDealModal/CreateDealModal')
)

interface Props {
  campaignId?: number
  className?: string
}

const READ_STATUS_SECTIONS: SectionedSection[] = [
  {
    key: 'read_status',
    items: [
      { label: 'Unread', value: 'unread' },
      { label: 'Read', value: 'read' },
    ],
  },
]

type Classification = 'all' | 'interested' | 'not_interested' | 'flagged'

const CLASSIFICATION_OPTIONS: {
  key: Classification
  label: string
  disabled?: boolean
}[] = [
  { key: 'all', label: 'All' },
  { key: 'interested', label: 'Interested' },
  { key: 'not_interested', label: 'Not Interested' },
  { key: 'flagged', label: 'Flagged' },
]

function ClassificationPills({
  selected,
  onChange,
  summary,
}: {
  selected: Classification
  onChange: (value: Classification) => void
  summary?: ReplySummary
}) {
  const getCount = (key: Classification): number | undefined => {
    if (!summary || !summary.is_available) return undefined
    switch (key) {
      case 'all':
        return summary.total
      case 'interested':
        return summary.interested
      case 'not_interested':
        return summary.not_interested
      case 'flagged':
        return summary.flagged
      default:
        return undefined
    }
  }

  return (
    <div className="flex gap-1.5 flex-wrap">
      {CLASSIFICATION_OPTIONS.map(({ key, label, disabled }) => {
        const count = getCount(key)
        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(key)}
            className={cn(
              'px-2.5 py-1 rounded-full text-xs transition-colors',
              disabled && 'opacity-40 cursor-not-allowed',
              selected === key
                ? 'bg-brand-hover text-fg-inverse'
                : 'bg-surface-alt text-fg-secondary hover:bg-surface-hover'
            )}
          >
            {label}
            {count !== undefined && ` (${count})`}
          </button>
        )
      })}
    </div>
  )
}

function ReplyListItem({
  reply,
  isSelected,
  onClick,
}: {
  reply: Reply
  isSelected: boolean
  onClick: () => void
}) {
  const displayName =
    reply.lead_full_name || reply.from_name || reply.from_email_address

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left px-4 py-3.5 border-b border-surface-alt transition-colors',
        isSelected ? 'bg-surface-hover/60' : 'hover:bg-surface-raised'
      )}
    >
      {/* Row 1: unread dot + name + timestamp */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 min-w-0">
          {!reply.is_read && (
            <span className="size-2 rounded-full bg-brand-hover shrink-0" />
          )}
          <span
            className={cn(
              'text-sm truncate',
              reply.is_read ? 'text-fg-secondary' : 'text-fg'
            )}
          >
            {displayName}
          </span>
          {reply.thread_count > 1 && (
            <span className="text-xxs text-fg-secondary shrink-0">
              ({reply.thread_count})
            </span>
          )}
        </div>
        <span className="text-xxs text-gray-400 shrink-0 ml-2">
          {reply.date_received ? formatRelativeTime(reply.date_received) : ''}
        </span>
      </div>

      {/* Row 2: company name */}
      {reply.lead_company && (
        <p className="text-xs text-gray-400 mb-0.5">{reply.lead_company}</p>
      )}

      {/* Row 3: body preview */}
      <p className="text-xs text-fg-secondary truncate mb-2">
        {reply.text_body}
      </p>

      {/* Row 4: badges + emails sent */}
      <div className="flex items-center gap-2 flex-wrap">
        {reply.is_interested === true && (
          <span className="inline-flex items-center gap-1 text-xxs px-1.5 py-0.5 rounded-full text-emerald-600 bg-emerald-50">
            <Star className="size-2.5" />
            Interested
          </span>
        )}
        {reply.is_interested === false && (
          <span className="inline-flex items-center gap-1 text-xxs px-1.5 py-0.5 rounded-full text-gray-600 bg-gray-100">
            <X className="size-2.5" />
            Not Interested
          </span>
        )}
        {reply.is_flagged && (
          <span className="inline-flex items-center gap-1 text-xxs px-1.5 py-0.5 rounded-full text-amber-600 bg-amber-50">
            <Flag className="size-2.5" />
            Flagged
          </span>
        )}
        <span className="text-xxs text-gray-400 ml-auto flex items-center gap-1">
          <Mail className="size-2.5" />
          {reply.emails_sent_count != null && reply.emails_sent_count > 0
            ? `${reply.emails_sent_count} sent`
            : 'No emails sent'}
        </span>
      </div>
    </button>
  )
}

function ReplyDetailHeader({
  reply,
  currentIndex,
  totalCount,
  onPrev,
  onNext,
}: {
  reply: Reply
  currentIndex: number
  totalCount: number
  onPrev: () => void
  onNext: () => void
}) {
  const isFirst = currentIndex === 0
  const isLast = currentIndex === totalCount - 1

  return (
    <div className="flex items-center gap-2 px-[20px] py-2 border-b border-line bg-surface-raised">
      <span className="inline-flex items-center text-xxs px-2 py-0.5 rounded-full bg-surface-hover text-brand-hover shrink-0">
        Step {reply.emails_sent_count || 1}
      </span>
      <span className="text-xs text-fg-secondary truncate min-w-0 flex-1">
        {reply.subject || '(no subject)'}
      </span>
      <span className="text-xs text-fg-secondary shrink-0 ml-2">
        {currentIndex + 1} of {totalCount}
      </span>
      <button
        type="button"
        disabled={isFirst}
        onClick={onPrev}
        className={cn(
          'size-7 inline-flex items-center justify-center rounded transition-colors shrink-0',
          isFirst
            ? 'text-fg-muted cursor-not-allowed'
            : 'text-fg hover:bg-surface-hover'
        )}
      >
        <ChevronLeft className="size-4" />
      </button>
      <button
        type="button"
        disabled={isLast}
        onClick={onNext}
        className={cn(
          'size-7 inline-flex items-center justify-center rounded transition-colors shrink-0',
          isLast
            ? 'text-fg-muted cursor-not-allowed'
            : 'text-fg hover:bg-surface-hover'
        )}
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  )
}

function ReplyClassificationRow({
  reply,
  onToggleInterested,
  onToggleFlag,
}: {
  reply: Reply
  onToggleInterested: () => void
  onToggleFlag: () => void
}) {
  return (
    <div className="shrink-0 px-[20px] py-3 border-t border-line">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Classification:</span>
          <button
            type="button"
            onClick={onToggleInterested}
            className={cn(
              'inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors',
              reply.is_interested === true
                ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
                : reply.is_interested === false
                  ? 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                  : 'text-gray-400 bg-surface-alt hover:bg-surface-hover'
            )}
          >
            {reply.is_interested === true ? (
              <>
                <Star className="size-3" />
                Interested
              </>
            ) : reply.is_interested === false ? (
              <>
                <X className="size-3" />
                Not Interested
              </>
            ) : (
              'Unclassified'
            )}
          </button>
        </div>
        <div className="w-px h-4 bg-line" />
        <button
          type="button"
          onClick={onToggleFlag}
          className={cn(
            'inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors',
            reply.is_flagged
              ? 'text-amber-600 border-amber-200 bg-amber-50'
              : 'text-gray-400 border-transparent hover:bg-surface-alt'
          )}
        >
          <Flag className="size-3" />
          {reply.is_flagged ? 'Flagged' : 'Flag for Follow-up'}
        </button>
      </div>
    </div>
  )
}

interface ReplyActions {
  onMarkRead: (replyId: number, isRead: boolean) => void
  onMarkAutomated: (replyId: number, isAutomated: boolean) => void
  onUnsubscribe: (replyId: number) => void
  onDelete: (replyId: number) => void
  onCreateDeal: (replyId: number) => void
}

/** Adapter: map a `Reply` to the shared `ThreadMessageData` shape. */
function replyToThreadMessageData(reply: Reply): ThreadMessageData {
  return {
    id: reply.id,
    sender: {
      name: reply.from_name || reply.lead_full_name || '',
      email: reply.from_email_address,
    },
    toRecipients: (reply.to_emails ?? []).map((r) => ({
      name: r.name || '',
      email: r.email_address,
    })),
    ccRecipients: (reply.cc_emails ?? []).map((r) => ({
      name: r.name || '',
      email: r.email_address,
    })),
    date: reply.date_received,
    htmlBody: reply.html_body,
    textBody: reply.text_body,
    attachments: (reply.attachments ?? []).map((a) => ({
      key: a.id,
      filename: a.file_name,
      // ReplyAttachment has no size/content_type on the current wire shape.
    })),
  }
}

/**
 * Build the kebab menu actions for a reply. Shared by ThreadMessage (in the
 * multi-message thread view) and the single-reply detail view so both expose
 * the same actions (Create deal, Mark read/unread, Unsubscribe, Delete, ...).
 */
function buildReplyMenuActions(reply: Reply, actions: ReplyActions) {
  return [
    {
      label: 'Create a deal',
      icon: TrendingUp,
      onClick: () => actions.onCreateDeal(reply.id),
      hasSeparatorAfter: true,
    },
    {
      label: reply.is_read ? 'Mark unread' : 'Mark read',
      icon: reply.is_read ? MailOpen : Mail,
      onClick: () => actions.onMarkRead(reply.id, !reply.is_read),
    },
    {
      label: reply.is_automated_reply ? 'Mark not automated' : 'Mark automated',
      icon: Bot,
      onClick: () =>
        actions.onMarkAutomated(reply.id, !reply.is_automated_reply),
    },
    {
      label: 'Unsubscribe contact',
      icon: UserMinus,
      onClick: () => actions.onUnsubscribe(reply.id),
      hasSeparatorAfter: true,
    },
    {
      label: 'Delete reply',
      icon: Trash2,
      onClick: () => actions.onDelete(reply.id),
      variant: 'destructive' as const,
    },
  ]
}

function ThreadMessage({
  reply,
  defaultExpanded,
  isLast,
  actions,
}: {
  reply: Reply
  defaultExpanded: boolean
  isLast: boolean
  actions: ReplyActions
}) {
  const menuItems = useMemo(
    () => buildReplyMenuActions(reply, actions),
    [reply, actions]
  )
  const data = useMemo(() => replyToThreadMessageData(reply), [reply])

  return (
    <ThreadMessageCard
      message={data}
      isLast={isLast}
      collapsible
      defaultExpanded={defaultExpanded}
      onExpand={() => {
        if (!reply.is_read) actions.onMarkRead(reply.id, true)
      }}
      menuItems={menuItems}
    />
  )
}

function ThreadMessageList({
  replies,
  actions,
}: {
  replies: Reply[]
  actions: ReplyActions
}) {
  const lastCountRef = useRef(replies.length)

  const scrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && replies.length !== lastCountRef.current) {
        node.scrollTop = node.scrollHeight
        lastCountRef.current = replies.length
      }
    },
    [replies.length]
  )

  return (
    <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
      {replies.map((reply, index) => (
        <ThreadMessage
          key={reply.id}
          reply={reply}
          defaultExpanded={index === replies.length - 1}
          isLast={index === replies.length - 1}
          actions={actions}
        />
      ))}
    </div>
  )
}

type ComposeMode = ReplyMode

/**
 * Whether the reply has enough distinct additional recipients that a
 * "Reply All" option is meaningful (beyond the workspace sender the reply
 * arrived on).
 */
function hasReplyAllRecipients(reply: Reply): boolean {
  return (
    filterSelf(
      [...(reply.to_emails ?? []), ...(reply.cc_emails ?? [])],
      reply.sender_email_address,
      (r) => r.email_address
    ).length > 0
  )
}

function ReplyActionBar({
  reply,
  onCompose,
  onTakeOver,
}: {
  reply: Reply
  onCompose: (mode: ComposeMode) => void
  onTakeOver: () => void
}) {
  const senderLabel = reply.sender_email_address?.split('@')[0] ?? 'outreach'
  const showReplyAll = hasReplyAllRecipients(reply)

  // Take-over is only available when the operator has at least one
  // connected work mailbox they can route the conversation through.
  const { data: integrationOptions } = useGetEmailIntegrationOptions()
  const hasWorkEmail = useMemo(
    () => (integrationOptions ?? []).some((opt) => opt.value > 0),
    [integrationOptions]
  )

  return (
    <div className="px-[20px] py-4 border-t border-line">
      <div className="flex items-center gap-4 pl-[52px]">
        <button
          type="button"
          onClick={() => onCompose('forward')}
          className="inline-flex items-center gap-2 h-8 px-3 rounded border border-line text-sm text-fg hover:bg-surface-alt transition-colors"
        >
          <CornerUpRight className="size-4" />
          Forward
        </button>
        {showReplyAll && (
          <button
            type="button"
            onClick={() => onCompose('reply_all')}
            className="inline-flex items-center gap-2 h-8 px-3 rounded border border-line text-sm text-fg hover:bg-surface-alt transition-colors"
          >
            <CornerUpRight className="size-4 -scale-x-100" />
            Reply All
          </button>
        )}
        <button
          type="button"
          onClick={() => onCompose('reply')}
          className="inline-flex items-center gap-2 h-8 px-3 rounded bg-brand-active text-fg-inverse text-sm hover:bg-brand transition-colors"
        >
          <Send className="size-4" />
          Reply from @{senderLabel}
        </button>
        <button
          type="button"
          onClick={onTakeOver}
          disabled={!hasWorkEmail}
          title={
            hasWorkEmail
              ? undefined
              : 'Connect a work email to take over conversations'
          }
          className="inline-flex items-center gap-2 h-8 px-3 rounded border border-brand text-sm text-brand hover:bg-brand-subtle transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowUpRight className="size-4" />
          Take over
        </button>
      </div>
    </div>
  )
}

function TakenOverPill({ reply }: { reply: Reply }) {
  const navigate = useNavigate()
  const buildCompanyUrl = useCompanyV2Url()

  // The "Open in inbox" CTA is only meaningful for users who can actually
  // see the take-over thread in their work mailbox. We approximate that
  // server-side check on the FE by matching `taken_over_by_email` against
  // the user's connected integration emails (own + shared) — the integration
  // option's `label` IS the email address.
  const { data: integrationOptions } = useGetEmailIntegrationOptions()
  const hasInboxAccess = useMemo(() => {
    const email = reply.taken_over_by_email?.toLowerCase()
    if (!email) return false
    return (integrationOptions ?? []).some(
      (opt) => opt.value > 0 && opt.label.toLowerCase() === email
    )
  }, [integrationOptions, reply.taken_over_by_email])

  const handleOpenInInbox = useCallback(async () => {
    if (reply.thread_id == null) {
      toast.error('Could not resolve the inbox link.')
      return
    }
    try {
      const link = await getReplyThreadInboxLink(reply.thread_id)
      const url = buildCompanyUrl(
        {
          company_type: link.contact_company.company_type ?? undefined,
          chain_id: link.contact_company.chain_id ?? undefined,
          university_id: link.contact_company.university_id ?? undefined,
          k12district_id: link.contact_company.k12district_id ?? undefined,
          lodging_id: link.contact_company.lodging_id ?? undefined,
          id: link.contact_company.id,
        },
        { email_thread_id: link.email_thread_id }
      )
      if (!url || url === '-') {
        toast.error('Could not resolve the inbox link.')
        return
      }
      navigate(url)
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        toast.error(
          'No inbox thread linked yet. Try again in a moment or check your work email.'
        )
        return
      }
      toast.error('Could not resolve the inbox link.')
    }
  }, [buildCompanyUrl, navigate, reply.thread_id])

  return (
    <div className="px-[20px] py-4 border-t border-line">
      <div className="flex items-center gap-3 pl-[52px] h-8">
        <span className="inline-flex items-center gap-2 text-sm text-fg-secondary">
          <ArrowUpRight className="size-4 text-brand" />
          Taken over by{' '}
          <strong className="text-fg">
            {reply.taken_over_by_name ?? 'someone'}
          </strong>
          {reply.taken_over_by_email && (
            <>
              {' '}
              with{' '}
              <strong className="text-fg">{reply.taken_over_by_email}</strong>
            </>
          )}
        </span>
        <button
          type="button"
          onClick={handleOpenInInbox}
          disabled={!hasInboxAccess}
          title={
            hasInboxAccess ? undefined : "You don't have access to this inbox"
          }
          className="inline-flex items-center gap-1 text-sm text-brand hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
        >
          Open in inbox
          <ChevronRight className="size-4" />
        </button>
      </div>
    </div>
  )
}

const composeSchema = z.object({
  body: z.string().min(1, 'Message body is required'),
})

type ComposeFormValues = z.infer<typeof composeSchema>

function ReplyComposePanel({
  reply,
  mode,
  integrationId,
  onDiscard,
  onSent,
  onModeChange,
}: {
  reply: Reply
  mode: ComposeMode
  /**
   * When set, the reply is sent via the operator's connected Nylas mailbox
   * (FIR-6879 spike). When null/undefined, the reply goes through the
   * EmailBison alias as before.
   */
  integrationId?: number | null
  onDiscard: () => void
  onSent: () => void
  /** Called from the inline-header mode switcher (Reply / Reply All / Forward). */
  onModeChange?: (mode: ReplyMode) => void
}) {
  const replyMutation = useReplyToReply()
  const forwardMutation = useForwardReply()
  const workEmailMutation = useTakeOver()
  const useWorkEmail = mode === 'reply' && integrationId != null
  const { data: integrationOptions } = useGetEmailIntegrationOptions()
  const workEmailLabel = useMemo(
    () => integrationOptions?.find((opt) => opt.value === integrationId)?.label,
    [integrationOptions, integrationId]
  )
  const { user } = useAuth()
  const userInitials =
    [user?.first_name, user?.last_name]
      .filter(Boolean)
      .map((n) => n![0].toUpperCase())
      .join('')
      .slice(0, 2) || 'UN'

  // Lazy initializers so reply_all's Cc pre-populate runs once at mount,
  // not on every render of the compose panel.
  const [forwardTo, setForwardTo] = useState<RecipientOption[]>([])
  const [ccTo, setCcTo] = useState<RecipientOption[]>(() =>
    mode === 'reply_all'
      ? buildReplyAllCcOptions(
          reply.to_emails ?? [],
          reply.cc_emails ?? [],
          reply.sender_email_address
        )
      : []
  )
  const [bccTo, setBccTo] = useState<RecipientOption[]>([])
  const [showCc, setShowCc] = useState(() => ccTo.length > 0)
  const [showBcc, setShowBcc] = useState(false)

  // Thread-context contacts surfaced in autocomplete regardless of search.
  const extraContacts = useMemo(
    () =>
      [...(reply.to_emails ?? []), ...(reply.cc_emails ?? [])].map((r) => ({
        email: r.email_address,
        name: r.name,
      })),
    [reply.to_emails, reply.cc_emails]
  )

  const {
    options: recipientOptions,
    isLoading: isLoadingRecipients,
    setSearch: setRecipientSearch,
  } = useRecipientOptions(extraContacts, false)

  const { control, handleSubmit } = useForm<ComposeFormValues>({
    defaultValues: { body: '' },
    resolver: zodResolver(composeSchema),
    mode: 'onSubmit',
  })

  const isSending =
    replyMutation.isPending ||
    forwardMutation.isPending ||
    workEmailMutation.isPending

  const onSubmit = handleSubmit((values) => {
    const cc_emails = ccTo.length ? toWireRecipients(ccTo) : undefined
    const bcc_emails = bccTo.length ? toWireRecipients(bccTo) : undefined

    if (mode === 'forward') {
      if (!forwardTo.length) return
      const payload: ForwardReplyPayload = {
        message: values.body,
        to_emails: toWireRecipients(forwardTo),
        cc_emails,
        bcc_emails,
      }
      forwardMutation.mutate(
        { replyId: reply.id, payload },
        { onSuccess: onSent }
      )
      return
    }

    if (useWorkEmail && integrationId != null) {
      const payload: TakeOverPayload = {
        integration_id: integrationId,
        message: values.body,
        cc_emails,
        bcc_emails,
      }
      if (reply.thread_id == null) {
        toast.error('Could not resolve the reply thread.')
        return
      }
      workEmailMutation.mutate(
        { threadId: reply.thread_id, payload },
        {
          onSuccess: () => {
            toast.success(`Sent from ${workEmailLabel ?? 'your work email'}`)
            onSent()
          },
          onError: (err) => {
            const detail = axios.isAxiosError(err)
              ? (err.response?.data as { detail?: string } | undefined)?.detail
              : undefined
            toast.error(detail ?? 'Could not send via your work email.')
          },
        }
      )
      return
    }

    const payload: ReplyToReplyPayload = {
      message: values.body,
      cc_emails,
      bcc_emails,
    }
    replyMutation.mutate({ replyId: reply.id, payload }, { onSuccess: onSent })
  })

  const baseSelectProps = useMemo(
    () => ({
      isMulti: true as const,
      options: recipientOptions,
      // react-select's native event — the inbox's CreatableSelect wrapper
      // renames this to `onSearchChange`, but we use ReactCreatableSelectWrapper
      // directly here, which forwards unknown props to react-select as-is.
      onInputChange: (input: string) => setRecipientSearch(input),
      isLoading: isLoadingRecipients,
      styles: emailRecipientStyles,
      formatOptionLabel: formatEmailRecipientOption,
      isValidNewOption: looksLikeEmail,
    }),
    [recipientOptions, isLoadingRecipients, setRecipientSearch]
  )

  /** Build a recipient row for To/Cc/Bcc — identical shape across the three. */
  const renderRow = (
    label: 'To' | 'Cc' | 'Bcc',
    value: RecipientOption[],
    setValue: React.Dispatch<React.SetStateAction<RecipientOption[]>>,
    opts: { onRemove?: () => void; trailing?: React.ReactNode } = {}
  ) => (
    <RecipientRow
      label={label}
      onRemove={opts.onRemove}
      trailing={opts.trailing}
    >
      <ReactCreatableSelectWrapper
        {...baseSelectProps}
        placeholder=""
        value={value}
        onChange={(val: unknown) => setValue((val ?? []) as RecipientOption[])}
        onCreateOption={(input: string) =>
          setValue((prev) => [...prev, { label: input, value: input }])
        }
      />
    </RecipientRow>
  )

  // Null when both toggles are already revealed so `RecipientRow` doesn't
  // render an empty trailing-slot wrapper that still eats `gap-2`.
  const toggles =
    showCc && showBcc ? null : (
      <CcBccToggles
        showCc={showCc}
        showBcc={showBcc}
        onShowCc={() => setShowCc(true)}
        onShowBcc={() => setShowBcc(true)}
      />
    )

  const baseLabel =
    mode === 'reply_all'
      ? `Replying to all on "${reply.subject || reply.from_email_address}"`
      : `Replying to ${reply.from_name || reply.from_email_address}`
  const inlineLabel = useWorkEmail
    ? `${baseLabel} from ${workEmailLabel ?? 'your work email'}`
    : baseLabel

  return (
    <InlineComposeShell userInitials={userInitials}>
      {useWorkEmail && (
        <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-fg-secondary bg-brand-subtle border-b border-line">
          <ArrowUpRight className="size-3 text-brand" />
          <span>Taking over</span>
        </div>
      )}
      {mode !== 'forward' && (
        <InlineReplyHeader
          mode={mode}
          label={inlineLabel}
          onModeChange={onModeChange}
          trailing={toggles}
        />
      )}

      <div className="px-3 pt-3 flex-1 min-h-0 overflow-y-auto">
        {mode === 'forward' &&
          renderRow('To', forwardTo, setForwardTo, { trailing: toggles })}

        {showCc &&
          renderRow('Cc', ccTo, setCcTo, {
            onRemove: () => {
              setShowCc(false)
              setCcTo([])
            },
          })}

        {showBcc &&
          renderRow('Bcc', bccTo, setBccTo, {
            onRemove: () => {
              setShowBcc(false)
              setBccTo([])
            },
          })}

        <StyledEditorRHF
          name="body"
          control={control}
          placeholder="Write your message..."
          borderless
          className="[&_.ql-editor]:!py-2 [&_.ql-editor]:!px-2 min-h-28"
          toolbarPrefix={
            <button
              type="button"
              onClick={onSubmit}
              disabled={isSending}
              className={cn(
                'inline-flex items-center gap-1.5 h-7 px-3 rounded text-xs text-fg-inverse transition-colors',
                isSending
                  ? 'bg-brand-active/60 cursor-not-allowed'
                  : 'bg-brand-active hover:bg-brand'
              )}
            >
              <Send className="size-3" />
              {isSending ? 'Sending...' : 'Send'}
            </button>
          }
          toolbarSuffix={
            <button
              type="button"
              onClick={onDiscard}
              className="inline-flex items-center gap-1 h-7 px-2 rounded text-xs text-fg-secondary hover:bg-surface-alt transition-colors"
            >
              Discard
            </button>
          }
          toolbarClassName="sticky bottom-0 bg-surface z-10"
        />
      </div>
    </InlineComposeShell>
  )
}

export function CampaignRepliesTab({ campaignId, className }: Props) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null)
  const [selectedThreadContext, setSelectedThreadContext] = useState<{
    leadId: number | null
    campaignId: number | null
  }>({ leadId: null, campaignId: null })
  const [senderFilter, setSenderFilter] = useState<string[]>([])
  const [campaignFilter, setCampaignFilter] = useState<string[]>([])
  const [readFilter, setReadFilter] = useState<string[]>([])
  const [classificationFilter, setClassificationFilter] =
    useState<Classification>('all')
  const [composeMode, setComposeMode] = useState<ComposeMode | null>(null)
  const [composeIntegrationId, setComposeIntegrationId] = useState<
    number | null
  >(null)
  // Reply id whose Take Over modal is open. Null when the modal is dismissed.
  const [takeOverTargetReplyId, setTakeOverTargetReplyId] = useState<
    number | null
  >(null)

  // Deep-link from the "interested reply" notification email. Captured once
  // on mount so URL clears (below) don't re-trigger the narrow. Owned by
  // this component (not the parent dashboard page) so dismissing actually
  // sticks when the user toggles back to this tab.
  const [searchParams, setSearchParams] = useSearchParams()
  const initialLinkedThreadId = useMemo<number | null>(() => {
    const raw = searchParams.get('thread')
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [linkedThreadId, setLinkedThreadId] = useState<number | null>(
    initialLinkedThreadId
  )
  // Set true after we resolve the thread one way or another (found or
  // toasted-as-missing) so we don't re-toast on every render.
  const linkedThreadHandledRef = useRef(false)
  const openCompose = useCallback(
    (mode: ComposeMode, integrationId: number | null = null) => {
      setComposeMode(mode)
      setComposeIntegrationId(mode === 'reply' ? integrationId : null)
    },
    []
  )
  const closeCompose = useCallback(() => {
    setComposeMode(null)
    setComposeIntegrationId(null)
  }, [])

  const [debouncedSearch] = useDebounce(searchQuery, 300)

  // When campaignId is provided, resolve bisonCampaignId from the sequence
  const { data: sequence, isLoading: isLoadingSequence } =
    useGetCampaignSequence(campaignId ?? 0)
  const bisonCampaignId = campaignId
    ? (sequence?.bison_campaign_id ?? 0)
    : undefined

  const clearLinkedThread = useCallback(() => {
    setLinkedThreadId(null)
    linkedThreadHandledRef.current = true
    // Strip ?thread= so refresh / re-mount doesn't re-narrow.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('thread')
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  // Base filters from the top-line dropdowns (used for both list and summary).
  // When a deep-link thread is active, force thread_id so the list narrows
  // to that single thread and ignores other filters; clearing the banner
  // restores normal dropdown behaviour.
  const baseFilters = useMemo<ReplyFilters>(() => {
    const f: ReplyFilters = {}
    if (bisonCampaignId !== undefined) f.campaign_id = bisonCampaignId
    if (campaignFilter.length)
      f.campaign = campaignFilter.map((id) => Number(id))
    if (debouncedSearch) f.search = debouncedSearch
    if (senderFilter.length) f.sender_email = senderFilter
    if (readFilter.length === 1) f.is_read = readFilter[0] === 'read'
    if (linkedThreadId !== null) f.thread_id = linkedThreadId
    return f
  }, [
    bisonCampaignId,
    campaignFilter,
    debouncedSearch,
    senderFilter,
    readFilter,
    linkedThreadId,
  ])

  // Full filters including classification pill (used for the list only)
  const filters = useMemo<ReplyFilters>(() => {
    const f = { ...baseFilters }
    if (classificationFilter === 'interested') f.is_interested = true
    if (classificationFilter === 'not_interested') f.is_interested = false
    if (classificationFilter === 'flagged') f.is_flagged = true
    return f
  }, [baseFilters, classificationFilter])

  const {
    data: repliesData,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetReplyThreadsInfinite(filters)
  const { data: summaryData } = useGetReplyThreadSummary(baseFilters)
  const { data: senderEmailsData } = useGetWorkspaceSenderEmails()
  const { data: campaignsData } = useGetCampaigns()

  const senderEmailSections = useMemo<SectionedSection[]>(
    () => [
      {
        key: 'sender_emails',
        items: (senderEmailsData?.results ?? []).map((se) => ({
          label: se.name ? `${se.name} (${se.email})` : se.email,
          value: se.email,
        })),
      },
    ],
    [senderEmailsData]
  )

  const campaignSections = useMemo<SectionedSection[]>(
    () => [
      {
        key: 'campaigns',
        items: (campaignsData?.results ?? []).map((c) => ({
          label: c.name,
          value: String(c.id),
        })),
      },
    ],
    [campaignsData]
  )

  const markReplyRead = useMarkReplyRead()
  const toggleFlag = useToggleThreadFlag()
  const markInterested = useMarkReplyInterested()
  const markAutomated = useMarkReplyAutomated()
  const unsubscribeContact = useUnsubscribeReplyContact()
  const deleteReply = useDeleteReply()

  // Create Deal modal context (resolved lazily when the user opens it).
  const [createDealContext, setCreateDealContext] =
    useState<ReplyContactCompany | null>(null)

  const handleCreateDeal = useCallback(
    async (replyId: number) => {
      try {
        const ctx = await getReplyContactCompany(replyId)
        if (!ctx.company_type_slug) {
          // Without a sub-category slug we can't pick the right Create Deal
          // form. Bail rather than opening the modal against a wrong default
          // (e.g. hospital company rendered as restaurants-and-bars).
          toast.error(
            'This company has no type set. Update the company type before creating a deal.'
          )
          return
        }
        setCreateDealContext(ctx)
      } catch (e) {
        // 404 = genuinely no Contact in the CRM for this reply's sender
        // (the resolver now falls back to email-match before raising, so
        // BisonLead drift is no longer a 404 cause). Body carries the
        // sender email when known so we can offer to import it.
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          const senderEmail = (
            e.response.data as { sender_email?: string } | undefined
          )?.sender_email
          toast.error(
            <span>
              No CRM contact found{senderEmail ? ` for ${senderEmail}` : ''}.{' '}
              <button
                type="button"
                onClick={() => navigate('/contacts')}
                className="underline font-medium hover:no-underline"
              >
                Open Contacts to import
              </button>
              .
            </span>,
            { autoClose: 8000 }
          )
        } else {
          toast.error('Could not open Create Deal. Please try again.')
        }
      }
    },
    [navigate]
  )

  const replyActions: ReplyActions = useMemo(
    () => ({
      onMarkRead: (replyId, isRead) =>
        markReplyRead.mutate({ replyId, isRead }),
      onMarkAutomated: (replyId, isAutomated) =>
        markAutomated.mutate({ replyId, isAutomated }),
      onUnsubscribe: async (replyId) => {
        const ok = await confirm(
          'Are you sure you want to unsubscribe this contact? They will no longer receive emails from this campaign.',
          'Unsubscribe Contact'
        )
        if (ok) unsubscribeContact.mutate(replyId)
      },
      onDelete: async (replyId) => {
        const ok = await confirm(
          'This reply will be permanently deleted.',
          'Delete Reply',
          '',
          { confirmLabel: 'Delete', buttonVariant: 'danger' }
        )
        if (ok) deleteReply.mutate(replyId)
      },
      onCreateDeal: handleCreateDeal,
    }),
    [
      markReplyRead,
      markAutomated,
      unsubscribeContact,
      deleteReply,
      handleCreateDeal,
    ]
  )

  const replies = useMemo(
    () => repliesData?.pages.flatMap((p) => p.results) ?? [],
    [repliesData]
  )
  const currentSelectedReply = replies.find(
    (r) => r.thread_id === selectedThreadId
  )
  // Keep last valid selection during refetches so the UI doesn't flash blank
  const selectedReplyRef = useRef<Reply | undefined>(undefined)
  if (currentSelectedReply) {
    selectedReplyRef.current = currentSelectedReply
  } else if (!selectedThreadId) {
    selectedReplyRef.current = undefined
  }
  const selectedReply = currentSelectedReply ?? selectedReplyRef.current

  // Stable adapter + menu data for the single-reply detail card, so
  // `ThreadMessageCard` memoization isn't defeated by fresh object identities
  // on every CampaignRepliesTab render (scroll, filter, compose toggle, ...).
  const singleReplyData = useMemo(
    () => (selectedReply ? replyToThreadMessageData(selectedReply) : undefined),
    [selectedReply]
  )
  const singleReplyMenu = useMemo(
    () =>
      selectedReply
        ? buildReplyMenuActions(selectedReply, replyActions)
        : undefined,
    [selectedReply, replyActions]
  )

  // Fetch all replies in the selected thread (uses stable context, not derived from selectedReply)
  const { data: threadData } = useGetThreadReplies(
    selectedThreadContext.leadId,
    selectedThreadContext.campaignId
  )
  const threadReplies = useMemo(
    () =>
      [...(threadData?.results ?? [])].sort(
        (a, b) =>
          new Date(a.date_received).getTime() -
          new Date(b.date_received).getTime()
      ),
    [threadData]
  )

  const { containerRef, sentinelRef } = useInfiniteScroll({
    onLoadMore: fetchNextPage,
    isLoading: isFetchingNextPage,
    hasMore: hasNextPage,
  })

  const handleSelectReply = useCallback(
    (reply: Reply) => {
      setSelectedThreadId(reply.thread_id)
      setSelectedThreadContext({
        leadId: reply.lead_id,
        campaignId: reply.campaign_id,
      })
      closeCompose()
      if (!reply.is_read) {
        markReplyRead.mutate({ replyId: reply.id, isRead: true })
      }
    },
    [markReplyRead, closeCompose]
  )

  // The narrowed thread row, when the deep-link is active. Drives the banner
  // copy and the auto-select. Pulled from the (already-fetched, narrowed)
  // list so we don't need a separate side-fetch.
  const linkedThreadRow =
    linkedThreadId !== null
      ? (replies.find((r) => r.thread_id === linkedThreadId) ?? null)
      : null

  // Once the narrowed list has actually returned a response, decide what to
  // do. Gate on `repliesData !== undefined` so we don't treat a not-yet-
  // enabled query (e.g. while waiting for the sequence to resolve
  // bisonCampaignId) as a settled empty result.
  //   - row found: leave the banner up; auto-select fires below.
  //   - empty result: toast and clear the link so the list goes back to all
  //     replies. handledRef prevents toast spam on every render.
  if (
    linkedThreadId !== null &&
    !linkedThreadHandledRef.current &&
    repliesData !== undefined
  ) {
    if (linkedThreadRow) {
      linkedThreadHandledRef.current = true
    } else if (replies.length === 0) {
      linkedThreadHandledRef.current = true
      queueMicrotask(() => {
        toast.info("That thread isn't available — showing all replies.")
        clearLinkedThread()
      })
    }
  }

  // One-shot deep-link auto-select: once the narrowed list has the thread,
  // open it. The ref keeps it from re-firing on refetch; we also skip when
  // the user has already picked a thread (race on slow networks). Setting
  // the ref before the microtask is intentional — strict-mode double renders
  // would otherwise queue two side-effect calls. Done inline (not via
  // useEffect) per `client/CLAUDE.md`.
  const didAutoSelectRef = useRef(false)
  if (
    linkedThreadRow !== null &&
    !didAutoSelectRef.current &&
    selectedThreadId === null
  ) {
    didAutoSelectRef.current = true
    queueMicrotask(() => handleSelectReply(linkedThreadRow))
  }

  const selectedIndex = replies.findIndex(
    (r) => r.thread_id === selectedThreadId
  )

  const handlePrevReply = useCallback(() => {
    if (selectedIndex > 0) {
      handleSelectReply(replies[selectedIndex - 1])
    }
  }, [selectedIndex, replies, handleSelectReply])

  const handleNextReply = useCallback(() => {
    if (selectedIndex < replies.length - 1) {
      handleSelectReply(replies[selectedIndex + 1])
    }
  }, [selectedIndex, replies, handleSelectReply])

  const hasActiveFilters =
    !!debouncedSearch ||
    senderFilter.length > 0 ||
    campaignFilter.length > 0 ||
    readFilter.length > 0 ||
    classificationFilter !== 'all'

  const formatFilterLabel = useCallback(
    (selected: string[], sections: SectionedSection[]) => {
      if (selected.length === 0) return 'All'
      if (selected.length <= 2) {
        return selected
          .map((val) => {
            for (const section of sections) {
              const item = section.items.find((i) => i.value === val)
              if (item) return item.label
            }
            return val
          })
          .join(', ')
      }
      return `${selected.length} selected`
    },
    []
  )

  // Only show full-page spinner on initial load, not on filter changes
  const isInitialLoad = isLoading && !repliesData
  const isWaitingForSequence = !!campaignId && isLoadingSequence

  if (isWaitingForSequence || isInitialLoad) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="size-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    )
  }

  // Empty state only when no filters are active and there are truly no replies
  if (replies.length === 0 && !hasActiveFilters) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-3 rounded-full bg-surface-muted p-4">
          <MessageSquare className="size-8 text-fg-muted" />
        </div>
        <p className="text-base text-fg">No replies yet</p>
        <p className="mt-1 text-sm text-fg-secondary">
          Replies from your campaign contacts will appear here.
        </p>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-4 flex-1 min-h-0', className)}>
      <div className="flex items-center gap-3 shrink-0">
        <div className="relative max-w-[360px] flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-muted" />
          <input
            type="text"
            placeholder="Search all replies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg bg-surface-muted py-2 pl-10 pr-4 text-sm text-fg outline-none placeholder:text-fg-muted focus:ring-2 focus:ring-brand/20"
          />
        </div>
        <SectionedMultiSelect
          buttonLabel="Sender"
          emptyLabel="Sender email"
          sections={senderEmailSections}
          selected={senderFilter}
          onApply={setSenderFilter}
          searchPlaceholder="Search senders..."
          formatLabel={formatFilterLabel}
        />
        {!campaignId && (
          <SectionedMultiSelect
            buttonLabel="Campaign"
            emptyLabel="Campaign"
            sections={campaignSections}
            selected={campaignFilter}
            onApply={setCampaignFilter}
            searchPlaceholder="Search campaigns..."
            formatLabel={formatFilterLabel}
          />
        )}
        <SectionedMultiSelect
          buttonLabel="Read status"
          emptyLabel="Read status"
          sections={READ_STATUS_SECTIONS}
          selected={readFilter}
          onApply={setReadFilter}
          searchable={false}
          formatLabel={formatFilterLabel}
        />
      </div>

      <ClassificationPills
        selected={classificationFilter}
        onChange={setClassificationFilter}
        summary={summaryData}
      />

      {linkedThreadId !== null && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-brand/20 bg-brand/5 px-4 py-2.5 text-sm">
          <Mail className="size-4 shrink-0 text-brand" />
          <span className="text-fg">Showing thread from email link</span>
          <button
            type="button"
            onClick={clearLinkedThread}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2.5 py-1 text-xs font-medium text-fg-secondary hover:bg-surface-hover hover:text-fg"
          >
            Show all replies
          </button>
        </div>
      )}

      <div className="flex flex-1 min-h-0 gap-0 overflow-hidden rounded-lg border border-line bg-surface">
        <div
          ref={containerRef}
          className="w-96 shrink-0 overflow-y-auto border-r border-line"
        >
          {replies.length === 0 && !isFetching ? (
            <div className="flex items-center justify-center py-12 text-sm text-fg-muted">
              No replies match your filters
            </div>
          ) : (
            <>
              {replies.map((reply) => (
                <ReplyListItem
                  key={reply.id}
                  reply={reply}
                  isSelected={selectedThreadId === reply.thread_id}
                  onClick={() => handleSelectReply(reply)}
                />
              ))}
              <div ref={sentinelRef} style={{ height: '1px' }} />
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-3">
                  <div className="size-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                </div>
              )}
            </>
          )}
        </div>

        {createDealContext && createDealContext.company_type_slug && (
          // Server returns sub_category.slug (a free-form string); trusted here
          // because a bad slug would've failed earlier pipeline steps. Single
          // `CompanyTypeSlug` cast is reused for `tableTypeSlug` since
          // `TableTypeSlug` is a strict superset of `CompanyTypeSlug`.
          <Suspense fallback={null}>
            <CreateDealModal
              show
              handleClose={() => setCreateDealContext(null)}
              onDealCreated={() => setCreateDealContext(null)}
              targetCompanyId={createDealContext.contact_company_id}
              totalSelectedRows={1}
              optimisticUiTarget="none"
              companyTypeSlug={
                createDealContext.company_type_slug as CompanyTypeSlug
              }
              tableTypeSlug={
                createDealContext.company_type_slug as CompanyTypeSlug
              }
            />
          </Suspense>
        )}

        <div className="flex flex-1 flex-col min-h-0 min-w-0 overflow-hidden">
          {selectedReply ? (
            <>
              <div className="shrink-0">
                <ReplyDetailHeader
                  reply={selectedReply}
                  currentIndex={selectedIndex}
                  totalCount={replies.length}
                  onPrev={handlePrevReply}
                  onNext={handleNextReply}
                />
              </div>
              {threadReplies.length > 1 ? (
                <ThreadMessageList
                  replies={threadReplies}
                  actions={replyActions}
                />
              ) : (
                singleReplyData && (
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <ThreadMessageCard
                      message={singleReplyData}
                      isLast
                      menuItems={singleReplyMenu}
                    />
                  </div>
                )
              )}
              <ReplyClassificationRow
                reply={selectedReply}
                onToggleInterested={() => {
                  markInterested.mutate({
                    replyId: selectedReply.id,
                    isInterested: selectedReply.is_interested !== true,
                  })
                }}
                onToggleFlag={() => {
                  if (selectedReply.thread_id) {
                    toggleFlag.mutate(selectedReply.thread_id)
                  }
                }}
              />
              {selectedReply.taken_over_thread_id != null ? (
                <TakenOverPill reply={selectedReply} />
              ) : composeMode ? (
                // The composite key remounts the panel when mode or sender
                // changes, so `useState(() => ...)` initializers re-run.
                <ReplyComposePanel
                  key={`${composeMode}-${composeIntegrationId ?? 'alias'}`}
                  reply={selectedReply}
                  mode={composeMode}
                  integrationId={composeIntegrationId}
                  onDiscard={closeCompose}
                  onSent={closeCompose}
                  onModeChange={(nextMode) => openCompose(nextMode, null)}
                />
              ) : (
                <ReplyActionBar
                  reply={selectedReply}
                  onCompose={openCompose}
                  onTakeOver={() => setTakeOverTargetReplyId(selectedReply.id)}
                />
              )}
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-fg-muted">
              Select a reply to view details
            </div>
          )}
        </div>
      </div>
      {selectedReply && (
        <TakeOverModal
          reply={selectedReply}
          show={takeOverTargetReplyId === selectedReply.id}
          onClose={() => setTakeOverTargetReplyId(null)}
          onConfirm={(integrationId) => {
            setTakeOverTargetReplyId(null)
            openCompose('reply', integrationId)
          }}
        />
      )}
    </div>
  )
}
